// apps/web/src/features/crm/deals/components/DealDetailDrawer.tsx
//
// Read-only right-side drawer for a LEGACY deal, deep-linked by the frozen
// request→deal `?openId=` chip (#2248 D-B1). Displays the deal projection +
// its linked requests + the create-linked-request action. The Deal write UI
// (stage controls, admin archive/restore) was retired — a v4 deal is a lead at
// `paid` and is worked on its lead card, not here. Reads via `useDeal(id)`.
//
// The drawer is purely informational — no form, no dirty state — so it
// uses `EditorDrawer` with `dirty={false}` to satisfy the project-wide
// rule that every drawer surface mounts through `EditorDrawer`. Mirrors
// `LeadDetailDrawer`.
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import type { DealListItem } from '@magimp/shared';
import {
  Button,
  EditorDrawer,
  ErrorState,
  LoadingState,
  StatusBadge,
} from '../../../../ui/index.js';
import { useAuthStore } from '../../../../stores/auth.js';
import { errorMessage } from '../../../../api/errors.js';
import { useDeal } from '../queries.js';
import { LinkedRequestsSection } from '../../components/LinkedRequestsSection.js';
import { useDealRequests } from '../../linkedRequests.js';
import { openLinkedRequest } from '../../linkedRequestsNav.js';
import { createRequestFromFunnel } from '../../createRequestFromFunnel.js';
import { canCreateRequest } from '../../../requests/display/index.js';

export interface DealDetailDrawerProps {
  dealId: string;
  onClose: () => void;
}

export function DealDetailDrawer({ dealId, onClose }: DealDetailDrawerProps) {
  const { t } = useTranslation(['deals', 'common']);
  // WHY (#1598): opt into `?includeRequestCount=true` for the "Requests"
  //   section count. Uses the `with-count` cache-key variant; default
  //   `useDeal(id)` readers are unaffected.
  const dealQ = useDeal(dealId, { includeRequestCount: true });

  return (
    <EditorDrawer dirty={false} onConfirmClose={onClose} width={420}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
        {dealQ.isPending && <LoadingState />}
        {dealQ.error && (
          <ErrorState
            title={t('common:states.loadError')}
            text={errorMessage(dealQ.error)}
            onRetry={() => void dealQ.refetch()}
          />
        )}
        {dealQ.isSuccess && <DealDetailBody deal={dealQ.data} />}
      </div>
    </EditorDrawer>
  );
}

function DealDetailBody({ deal }: { deal: DealListItem }) {
  const { t } = useTranslation(['deals', 'common', 'crm']);
  const navigate = useNavigate();
  const perms = useAuthStore((s) => s.permissions);
  const user = useAuthStore((s) => s.user);
  const requestsQ = useDealRequests(deal.id);
  // WHY (#1599): mirror the request workspace's create gate
  //   (`requests.manage`) so the funnel action only shows when the create
  //   POST would be authorised.
  const canCreateLinkedRequest = canCreateRequest(perms) && user !== null;
  return (
    <>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, flex: 1, minWidth: 0 }}>
          {deal.publicCode}
        </h2>
        {deal.archivedAt && <StatusBadge status="archived" size="sm" />}
      </header>
      <div style={{ fontSize: 14 }} data-testid="deal-detail-title">
        {deal.title}
      </div>
      <Field label={t('deals:fields.client')}>
        <Link to={`/clients/${deal.clientId}`} style={{ fontWeight: 500 }}>
          {deal.client.name}
        </Link>
      </Field>
      <Field label={t('deals:fields.manager')}>{deal.manager.name}</Field>
      <Field label={t('deals:fields.lead')}>
        {deal.leadId ? (
          <Link to={`/leads`} style={{ fontWeight: 500 }} data-testid="deal-detail-lead-link">
            {deal.leadId}
          </Link>
        ) : (
          <em>{t('deals:fields.unlinked')}</em>
        )}
      </Field>
      {canCreateLinkedRequest && user && (
        <Button
          kind="secondary"
          onClick={() =>
            createRequestFromFunnel(navigate, {
              dealId: deal.id,
              ...(deal.leadId != null ? { leadId: deal.leadId } : {}),
            })
          }
          data-testid="deal-create-request-action"
        >
          {t('crm:requests.createAction')}
        </Button>
      )}
      <LinkedRequestsSection
        count={deal.requestCount ?? 0}
        rows={requestsQ.data?.items ?? []}
        isLoading={requestsQ.isPending}
        isError={requestsQ.isError}
        onOpenRequest={(requestId) => openLinkedRequest(navigate, requestId)}
      />
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--fg-2)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{children}</div>
    </div>
  );
}
